// Lower Leg (Tibia / Shin / Calf) Template Configuration
// Questions simplified to 12-year-old reading level

export const tibiaTemplate = {
  id: 'TIBIA',
  name: 'Lower Leg (Tibia / Shin / Calf)',
  icon: '🦵',
  dominantSide: 'leg',

  fields: {
    injuredSide: {
      type: 'radio',
      label: 'Which leg has the problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    injuryDate: {
      type: 'date',
      label: 'When did it start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly', 'Gradually with running', 'After recent increase in training'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Aching inside shin',
        'Specific sore spot on bone',
        'Pain during running that goes away at rest',
        'Pain builds during exercise and lasts after',
        'Tightness or pressure in shin',
        'Numbness or pins and needles in foot',
        'Calf pain or tightness',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Please describe the other problem:',
      placeholder: 'Tell us about your other problem',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how your lower leg problem started',
      rows: 3,
      required: true
    },

    // Pain Section
    painLocation: {
      type: 'radio',
      label: 'Where exactly is the pain?',
      options: [
        'Diffuse aching along inside of shin (more than 5cm area)',
        'Specific sore point on bone (less than 5cm area)',
        'Front of shin',
        'Outside of shin',
        'Back of lower leg (calf)',
        'Whole shin area'
      ]
    },

    painPattern: {
      type: 'radio',
      label: 'Which best describes your pain pattern?',
      options: [
        'Pain at start of running that improves as I warm up',
        'Pain that builds during running and gets worse',
        'Pain at rest and at night',
        'Pain only during running, gone within minutes of stopping'
      ]
    },

    nightPain: {
      type: 'radio',
      label: 'Do you have pain at night when resting?',
      options: ['Yes', 'No']
    },

    painWithHopping: {
      type: 'radio',
      label: 'Does it hurt to hop on the sore leg?',
      options: ['Yes', 'No', 'Haven\'t tried']
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painCharacter: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Sharp', 'Aching', 'Throbbing', 'Burning', 'Pressure', 'Other']
    },

    painCharacterOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painCharacter', includes: 'Other' }
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Running',
        'Walking',
        'Jumping',
        'Going up hills',
        'Hard surfaces',
        'Standing for a long time',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Ice',
        'Compression',
        'Elevation',
        'Pain medication',
        'Gentle stretching',
        'Soft surfaces',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Training Section
    sport: {
      type: 'text',
      label: 'What sports or exercise do you do?',
      placeholder: 'e.g., running, football, netball, gym'
    },

    runningSurface: {
      type: 'checkbox-multiple',
      label: 'What surfaces do you usually run or train on?',
      options: [
        'Road',
        'Trail',
        'Track',
        'Treadmill',
        'Grass',
        'Other'
      ]
    },

    runningSurfaceOther: {
      type: 'text',
      label: 'What other surface do you train on?',
      placeholder: 'Tell us what surface',
      showWhen: { field: 'runningSurface', includes: 'Other' }
    },

    weeklyMileage: {
      type: 'text',
      label: 'How far do you run each week?',
      placeholder: 'e.g., 30km per week, 5 runs of 5km'
    },

    recentIncrease: {
      type: 'radio',
      label: 'Have you recently increased your training?',
      options: [
        'Yes, more distance/volume',
        'Yes, harder/faster intensity',
        'Both more distance and harder',
        'No'
      ]
    },

    newFootwear: {
      type: 'radio',
      label: 'Have you recently changed your shoes or started new footwear?',
      options: ['Yes', 'No']
    },

    previousStressInjury: {
      type: 'radio',
      label: 'Have you had a stress injury before (stress fracture, shin splints)?',
      options: ['Yes', 'No']
    },

    // Compartment Syndrome Screen Section
    tightnessWithRunning: {
      type: 'radio',
      label: 'Does your shin feel very tight or full of pressure during running?',
      options: ['Yes', 'No']
    },

    numbnessWithRunning: {
      type: 'radio',
      label: 'Do you get numbness or pins and needles in your foot during running?',
      options: ['Yes', 'No']
    },

    weaknessOrFootDrop: {
      type: 'radio',
      label: 'Does your foot feel weak or floppy during or after running?',
      options: ['Yes', 'No']
    },

    symptomsResolveOnStopping: {
      type: 'radio',
      label: 'When you stop running, how quickly do these symptoms go away?',
      options: [
        'Within minutes',
        'Within 30 minutes',
        'Takes hours',
        'No, they are persistent'
      ]
    },

    // RED-S / Bone Health Section
    menstrualStatus: {
      type: 'radio',
      label: 'For females: How are your periods?',
      options: [
        'Regular',
        'Irregular',
        'No periods for 3 or more months',
        'Not applicable',
        'Prefer not to say'
      ]
    },

    dietaryConcerns: {
      type: 'radio',
      label: 'Do you have any concerns about your diet or eating habits?',
      options: ['Yes', 'No', 'Prefer not to say']
    },

    previousStressFracture: {
      type: 'radio',
      label: 'Have you ever had a stress fracture?',
      options: ['Yes', 'No']
    },

    historyOfFracture: {
      type: 'radio',
      label: 'Have you broken any bones before?',
      options: ['Yes', 'No']
    },

    vitDCalciumIntake: {
      type: 'text',
      label: 'Do you take vitamin D or calcium supplements? How much dairy do you eat?',
      placeholder: 'e.g., 2 glasses of milk a day, no supplements'
    },

    // Treatment
    medications: {
      type: 'textarea',
      label: 'What treatments have you tried so far?',
      placeholder: 'e.g., rest, physiotherapy, orthotics, pain medication',
      rows: 2
    },

    imaging: {
      type: 'radio',
      label: 'Have you had any scans or X-rays for this problem?',
      options: ['Yes', 'No']
    },

    imagingType: {
      type: 'text',
      label: 'What type of scan?',
      placeholder: 'e.g., X-ray, MRI, bone scan, ultrasound',
      showWhen: { field: 'imaging', equals: 'Yes' }
    },

    imagingWhen: {
      type: 'text',
      label: 'When was the scan?',
      placeholder: 'e.g., 2 weeks ago',
      showWhen: { field: 'imaging', equals: 'Yes' }
    },

    imagingResults: {
      type: 'textarea',
      label: 'What did the scan show?',
      placeholder: 'Tell us what the results were, or write "I don\'t know"',
      rows: 2,
      showWhen: { field: 'imaging', equals: 'Yes' }
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'injury',
      title: '2. About Your Lower Leg Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injuredSide', 'onsetType', 'mainProblem', 'mainProblemOther', 'injuryDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painPattern', 'nightPain', 'painWithHopping', 'painSeverity', 'painCharacter', 'painCharacterOther', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'training',
      title: '4. Your Training',
      component: 'GenericSection',
      enabled: true,
      fields: ['sport', 'runningSurface', 'runningSurfaceOther', 'weeklyMileage', 'recentIncrease', 'newFootwear', 'previousStressInjury']
    },
    {
      id: 'compartment',
      title: '5. Tightness & Pressure Symptoms',
      component: 'GenericSection',
      enabled: true,
      fields: ['tightnessWithRunning', 'numbnessWithRunning', 'weaknessOrFootDrop', 'symptomsResolveOnStopping']
    },
    {
      id: 'redFlags',
      title: '6. Important Safety Questions',
      component: 'RedFlagsSection',
      enabled: true,
      fields: [
        {
          id: 'unableToWeightBear',
          type: 'radio',
          label: 'Are you unable to put weight on your leg?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 6
        },
        {
          id: 'severeNightPain',
          type: 'radio',
          label: 'Do you have severe pain at night that wakes you up?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'progressiveSymptoms',
          type: 'radio',
          label: 'Are your symptoms getting worse even though you have stopped running?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 4
        },
        {
          id: 'numbnessAfterRunning',
          type: 'radio',
          label: 'Do you get numbness or tingling in your foot that lasts after you stop running?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 6
        },
        {
          id: 'feverChills',
          type: 'radio',
          label: 'Do you have a fever or chills?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 4
        },
        {
          id: 'historyOfCancer',
          type: 'radio',
          label: 'Have you ever had cancer?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'unexplainedWeightLoss',
          type: 'radio',
          label: 'Have you lost weight without trying?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'calfSwellingDVT',
          type: 'radio',
          label: 'Is your calf swollen, warm, and painful (especially one side only)?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 8
        }
      ],
      alerts: {
        critical: {
          threshold: 8,
          message: 'URGENT: These symptoms need immediate medical attention. Please go to the Emergency Department.'
        },
        urgent: {
          threshold: 6,
          message: 'Please contact us promptly about these symptoms.'
        },
        concerning: {
          threshold: 4,
          message: 'We will review your answers carefully.'
        }
      }
    },
    {
      id: 'bonehealth',
      title: '7. Bone Health',
      component: 'GenericSection',
      enabled: true,
      fields: ['menstrualStatus', 'dietaryConcerns', 'previousStressFracture', 'historyOfFracture', 'vitDCalciumIntake']
    },
    {
      id: 'treatment',
      title: '8. Previous Treatment',
      component: 'GenericSection',
      enabled: true,
      fields: ['medications', 'imaging', 'imagingType', 'imagingWhen', 'imagingResults']
    },
    {
      id: 'medical',
      title: '9. Your Health',
      component: 'MedicalHistorySection',
      enabled: true
    },
    {
      id: 'exerciseVitalSign',
      title: '10. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },
    {
      id: 'goals',
      title: '11. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly': 'acute onset',
      'Gradually with running': 'gradual onset with running load',
      'After recent increase in training': 'onset following training load increase'
    },
    commonDiagnoses: [
      'Tibial stress reaction (Fredericson Grade 1-2)',
      'Tibial stress fracture (Fredericson Grade 3-4)',
      'Tibial stress fracture - medial cortex',
      'Tibial stress fracture - anterior cortex (high-risk)',
      'Medial tibial stress syndrome (MTSS / shin splints)',
      'Chronic exertional compartment syndrome - anterior',
      'Chronic exertional compartment syndrome - lateral',
      'Chronic exertional compartment syndrome - deep posterior',
      'Chronic exertional compartment syndrome - superficial posterior',
      'Tibialis posterior tendinopathy',
      'Periostitis',
      'Soleus strain',
      'Gastrocnemius strain',
      'Popliteal artery entrapment',
      'Referred lumbar/knee pathology'
    ]
  }
};

export default tibiaTemplate;
